/**
 * @fileoverview Inline "Edit Target" form for TenantDetailModal.
 * @description Lets the owner's reporting Manager/Head or Super Admin correct an open target's
 * name, period and priority (enforced again, defense-in-depth, by the mock API itself). The period
 * uses the same type + specific-period pickers as Assign Target, with start/end dates derived via
 * period.utils.ts's computePeriod and previewed before saving. Owner changes stay with Reassign.
 * Rendered only while open (TenantDetailModal mounts it conditionally), so every open starts from
 * the target's current values with a fresh mutation state.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { PRIORITY_OPTIONS } from '@/features/sales/constants/sales-options.constants'
import { useUpdateTargetDetails } from '@/features/sales/hooks/useUpdateTargetDetails'
import {
  computePeriod,
  periodOptionsFor,
  periodValueForDate,
} from '@/features/sales/lib/period.utils'
import {
  editTargetFormSchema,
  type EditTargetFormValues,
} from '@/features/sales/schemas/edit-target-form.schema'
import Label from '@/shared/components/atoms/Label'
import Select from '@/shared/components/atoms/Select'
import FormField from '@/shared/components/molecules/FormField'

import DialogActions from './DialogActions'

import type { PeriodOption } from '@/features/sales/lib/period.utils'
import type { Target, TargetPeriodType } from '@/features/sales/types/tenant.types'
import type { ChangeEvent, FormEvent } from 'react'

const PERIOD_TYPES: TargetPeriodType[] = ['Monthly', 'Quarterly', 'Half-Yearly', 'Annual']

interface EditTargetFormProps {
  tenantId: string
  target: Target
  actingUserId: string
  actingUserRole: string
  onUpdated: () => void
  onCancel: () => void
}

/**
 * @description Builds the period dropdown's options for a period type, making sure the target's
 * own current period is always selectable — periodOptionsFor only spans the current and next
 * financial year, and an older target's period must not silently change just by opening the form.
 * @param {TargetPeriodType} periodType - The period type currently selected in the form.
 * @param {Target} target - The target being edited.
 * @returns {PeriodOption[]} The selectable periods.
 */
function periodChoicesFor(periodType: TargetPeriodType, target: Target): PeriodOption[] {
  const options = periodOptionsFor(periodType)
  if (periodType !== target.periodType) return options

  const currentValue = periodValueForDate(target.periodType, target.startDate)
  return options.some((option) => option.value === currentValue)
    ? options
    : [{ value: currentValue, label: target.periodLabel }, ...options]
}

/**
 * @component
 * @param {EditTargetFormProps} props - The target to edit and the acting user's identity.
 * @param {string} props.tenantId - The tenant the target belongs to, for query invalidation.
 * @param {Target} props.target - The target being edited; its current values pre-fill the form.
 * @param {string} props.actingUserId - The signed-in viewer's sales-roster id.
 * @param {string} props.actingUserRole - The signed-in viewer's active role name.
 * @param {() => void} props.onUpdated - Called after a successful update.
 * @param {() => void} props.onCancel - Called to dismiss the form without updating.
 */
function EditTargetForm({
  tenantId,
  target,
  actingUserId,
  actingUserRole,
  onUpdated,
  onCancel,
}: EditTargetFormProps) {
  const { t } = useTranslation()
  const updateTarget = useUpdateTargetDetails(tenantId)
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<EditTargetFormValues>({
    resolver: zodResolver(editTargetFormSchema),
    defaultValues: {
      name: target.name,
      periodType: target.periodType,
      periodValue: periodValueForDate(target.periodType, target.startDate),
      priority: target.priority,
    },
  })

  const periodType = useWatch({ control, name: 'periodType' })
  const periodValue = useWatch({ control, name: 'periodValue' })
  const periodChoices = periodChoicesFor(periodType, target)
  const preview = periodValue ? computePeriod(periodType, periodValue) : null

  /**
   * @description Keeps the selected period valid when the period type changes — back to the
   * target's own period when returning to its original type, otherwise that type's first option.
   * @param {ChangeEvent<HTMLSelectElement>} event - The period-type select's change event.
   */
  const onPeriodTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextType = event.target.value as TargetPeriodType
    setValue(
      'periodValue',
      nextType === target.periodType
        ? periodValueForDate(target.periodType, target.startDate)
        : (periodOptionsFor(nextType)[0]?.value ?? ''),
    )
  }

  /**
   * @description Derives the period's dates and submits the update.
   * @param {EditTargetFormValues} values - The validated form values.
   */
  const onValid = (values: EditTargetFormValues) => {
    const period = computePeriod(values.periodType, values.periodValue)
    updateTarget.mutate(
      {
        targetId: target.id,
        input: {
          name: values.name,
          periodType: values.periodType,
          periodLabel: period.periodLabel,
          startDate: period.startDate,
          endDate: period.endDate,
          priority: values.priority,
          actingUserId,
          actingUserRole,
        },
      },
      { onSuccess: onUpdated },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="edit-target-form"
      className="flex flex-col gap-3 rounded-lg border border-row-border bg-surface-hover p-3.5"
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid="edit-target-title">
        {t('sales.tenantDetail.editTarget.title')}
      </span>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="col-span-full">
          <FormField
            id="editTargetName"
            data-testid="edit-target-name-input"
            size="md"
            label={t('sales.tenantDetail.editTarget.fields.name')}
            error={errors.name && t(errors.name.message ?? '')}
            {...register('name')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="editTargetPeriodType">
            {t('sales.tenantDetail.editTarget.fields.periodType')}
          </Label>
          <Select
            id="editTargetPeriodType"
            data-testid="edit-target-period-type-select"
            options={PERIOD_TYPES.map((type) => ({
              value: type,
              label: t(`sales.pipeline.assign.periodTypes.${type}`),
            }))}
            {...register('periodType', { onChange: onPeriodTypeChange })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="editTargetPeriod">
            {t('sales.tenantDetail.editTarget.fields.period')}
          </Label>
          <Select
            id="editTargetPeriod"
            data-testid="edit-target-period-select"
            invalid={Boolean(errors.periodValue)}
            options={periodChoices}
            {...register('periodValue')}
          />
          {errors.periodValue && (
            <span
              className="text-[11.5px] font-medium text-danger"
              data-testid="edit-target-period-error"
            >
              {t(errors.periodValue.message ?? '')}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="editTargetPriority">
            {t('sales.tenantDetail.editTarget.fields.priority')}
          </Label>
          <Select
            id="editTargetPriority"
            data-testid="edit-target-priority-select"
            options={PRIORITY_OPTIONS.map((option) => ({
              value: option,
              label: t(`sales.pipeline.assign.priorities.${option}`),
            }))}
            {...register('priority')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-semibold text-cell">
            {t('sales.tenantDetail.editTarget.fields.dates')}
          </span>
          <span
            className="flex h-[38px] items-center text-[13px] font-medium text-ink"
            data-testid="edit-target-dates"
          >
            {preview ? `${preview.startDate} – ${preview.endDate}` : '—'}
          </span>
        </div>
      </div>

      <DialogActions
        testIdPrefix="edit-target"
        cancelLabel={t('sales.pipeline.form.cancel')}
        confirmLabel={t('sales.tenantDetail.editTarget.submit')}
        onCancel={onCancel}
        confirmType="submit"
        isLoading={updateTarget.isPending}
        disabled={false}
      />

      {updateTarget.isError && (
        <p role="alert" className="text-[12.5px] text-danger" data-testid="edit-target-error">
          {updateTarget.error instanceof Error
            ? updateTarget.error.message
            : String(updateTarget.error)}
        </p>
      )}
    </form>
  )
}

export default EditTargetForm
